import { createApiClient } from './client'

export type SubscriptionTier = 'free' | 'pro'

export interface StorageStatus {
  usedBytes: number
  limitBytes: number
  remainingBytes: number
  usedPercent: number
  tier: SubscriptionTier
}

export interface SubscriptionLimits {
  tripLimit: number // -1 = unlimited
  crewLimit: number // -1 = unlimited
  vehicleLimit: number // -1 = unlimited
  photoLimit: number // -1 = unlimited
}

export interface SubscriptionFeatures {
  canCreateTrip: boolean
  canAddCrewMember: boolean
  canAddVehicle: boolean
  canUploadPhoto: boolean
  canUseSnapToRoad: boolean
  canExportGpx: boolean
  canUseDispersedCampingFinder: boolean
  canUseRanger: boolean
  canUse3dRoutePreview: boolean
  canUseFullSharingPermissions: boolean
}

export interface SubscriptionStatus {
  tier: SubscriptionTier
  limits: SubscriptionLimits
  features: SubscriptionFeatures
}

export interface LimitCheckResult {
  allowed: boolean
  current: number
  limit: number
  isUnlimited: boolean
}

export class SubscriptionsApi {
  private apiClient: ReturnType<typeof createApiClient>

  constructor(getAuthToken: () => Promise<string | null>) {
    this.apiClient = createApiClient(getAuthToken)
  }

  async getStatus(): Promise<SubscriptionStatus> {
    const response = await this.apiClient.get<{
      success: boolean
      data: SubscriptionStatus
    }>('/subscriptions/status')
    return response.data
  }

  async getTripLimit(): Promise<LimitCheckResult> {
    const response = await this.apiClient.get<{
      success: boolean
      data: LimitCheckResult
    }>('/subscriptions/limits/trip')
    return response.data
  }

  async getCrewLimit(tripId: string): Promise<LimitCheckResult> {
    const response = await this.apiClient.get<{
      success: boolean
      data: LimitCheckResult
    }>('/subscriptions/limits/crew', { tripId })
    return response.data
  }

  async getVehicleLimit(): Promise<LimitCheckResult> {
    const response = await this.apiClient.get<{
      success: boolean
      data: LimitCheckResult
    }>('/subscriptions/limits/vehicle')
    return response.data
  }

  async getStorageStatus(): Promise<StorageStatus> {
    const response = await this.apiClient.get<{
      success: boolean
      data: StorageStatus
    }>('/subscriptions/storage')
    return response.data
  }

  async createCheckoutSession(interval: 'monthly' | 'annual'): Promise<{ url: string }> {
    const base = window.location.origin
    const response = await this.apiClient.post<{ success: boolean; data: { url: string } }>(
      '/subscriptions/stripe/checkout',
      {
        interval,
        successUrl: `${base}/pricing?checkout=success`,
        cancelUrl: `${base}/pricing`,
      }
    )
    return response.data
  }

  async createPortalSession(): Promise<{ url: string }> {
    const base = window.location.origin
    const response = await this.apiClient.post<{ success: boolean; data: { url: string } }>(
      '/subscriptions/stripe/portal',
      { returnUrl: `${base}/pricing` }
    )
    return response.data
  }
}

